import React, { useState } from "react";
import { Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ButtonLoading } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { useKeepSendingRequest } from "go-core/hooks/useKeepSendingRequest";
import { UseKeepSendingRequestState } from "go-core/hooks/utils/types";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { hasPermission } from "go-security/services/organizations/selectors";
import { apiOrganization } from "../../../../../../../../../services/Api/Organization/apiOrganization";
import { TransactionApi } from "../../../../../../../../../services/Api/Organization/types";
import ChangeTransactionPaymentMethod from "../../../components/ChangeTransactionPaymentMethod";
import { transactionPreviewParams } from "./utils";

interface Props {
	transaction: TransactionApi;
	canChangePaymentMethod?: boolean;
	handleSuccessAction?: () => void;
	setTransaction: React.Dispatch<React.SetStateAction<TransactionApi | undefined>>;
	setPreventClosingPreview?: React.Dispatch<React.SetStateAction<boolean>>;
}

const TransactionPreviewActions = ({
	transaction,
	canChangePaymentMethod,
	handleSuccessAction,
	setTransaction,
	setPreventClosingPreview,
}: Props) => {
	const { t } = useTranslation();
	const confirmation = useConfirmation();
	const { addFlash, addSuccessFlash } = useFlash();
	const [refundLoading, setRefundLoading] = useState(false);
	const hasVenueTransactionRefundPermission = useSelector(hasPermission("VENUE_TRANSACTION_REFUND"));
	const [showChangePaymentMethodModal, setShowChangePaymentMethodModal] = useState(false);

	const getCanChangePaymentMethod = () => {
		if (transaction?.contexts?.some((context) => context.type === "REPORT_PAID")) return false;
		if ((transaction?.status !== "NEW" && transaction?.status !== "SUCCESS") || transaction?.type !== "PAY_IN")
			return false;
		return canChangePaymentMethod;
	};

	const canMakeRefund = () => {
		if (transaction.refundable && hasVenueTransactionRefundPermission) return true;
		return false;
	};

	const handleRefund = async (sendingRequestState?: UseKeepSendingRequestState) => {
		const { isSendingRequests, allRequestsSent, forceStopSendingRequests } = sendingRequestState ?? {};
		let firstRequestStartTimestamp = 0;
		setRefundLoading(true);
		setPreventClosingPreview?.(true);
		try {
			if (!isSendingRequests) {
				await confirmation({
					title: t("lib:confirmation.title"),
					message: t("lib:confirmation.message.action"),
				});
			}
			firstRequestStartTimestamp = performance.now();
			const res = await apiOrganization.refundTransaction(transaction.id, transactionPreviewParams);
			addSuccessFlash(t("lib:common.flash.completed"));
			handleSuccessAction?.();
			setTransaction(res);
		} catch (err: any) {
			const wrongOrderTerminalError = err.errors?.find(
				(error: Record<string, any>) => error.code === "wrong_order_terminal"
			);
			if (wrongOrderTerminalError) {
				if (!isSendingRequests) {
					keepSendingRequests(firstRequestStartTimestamp);
				}

				if (allRequestsSent) {
					const flashMessage = t("constraints.wrong_terminal_order", {
						terminal_name: wrongOrderTerminalError.terminal_name,
					});
					addFlash({
						type: "danger",
						msg: flashMessage,
						messageAlreadyCreated: true,
					});
				} else {
					return;
				}
			} else {
				handleError.alert(err, addFlash);
			}
		}
		if (forceStopSendingRequests !== undefined) {
			forceStopSendingRequests.current = true;
		}
		setRefundLoading(false);
		setPreventClosingPreview?.(false);
	};

	const { keepSendingRequests } = useKeepSendingRequest((sendingRequestState: UseKeepSendingRequestState) =>
		handleRefund(sendingRequestState)
	);

	const handleChangePaymentMethod = () => {
		setPreventClosingPreview?.(true);
		setShowChangePaymentMethodModal(true);
	};

	const handleHideChangePaymentMethodModal = () => {
		setPreventClosingPreview?.(false);
		setShowChangePaymentMethodModal(false);
	};

	if (!getCanChangePaymentMethod() && !canMakeRefund()) return <></>;

	return (
		<>
			<div className="w-100 mb-3 d-flex flex-column">
				<div className="w-100 transaction-actions-grid">
					{getCanChangePaymentMethod() && (
						<div className="d-grid">
							<Button variant="add" className="mb-0 w-100" onClick={handleChangePaymentMethod}>
								{t("modules.transaction.action.change_payment_method.title")}
							</Button>
						</div>
					)}
					{canMakeRefund() && (
						<div className="d-grid">
							<ButtonLoading
								loading={refundLoading}
								variant="add"
								className="mb-0 w-100"
								onClick={() => handleRefund()}
								disabled={!transaction.payment_processed}
							>
								{t("modules.transaction.action.refund.title")}
							</ButtonLoading>
						</div>
					)}
				</div>
				{canMakeRefund() && !transaction.payment_processed && (
					<p className="text-muted mt-2 refund-information">
						{t("modules.transaction.field.refund_is_not_possible_yet.title")}
					</p>
				)}
			</div>
			{showChangePaymentMethodModal && (
				<ChangeTransactionPaymentMethod
					transaction={transaction}
					show={showChangePaymentMethodModal}
					onHide={handleHideChangePaymentMethodModal}
					id={transaction.id}
					handleSuccessAction={(item) => {
						if (handleSuccessAction) handleSuccessAction();
						setTransaction(item as TransactionApi);
					}}
					documentNumber={transaction.id.toString()}
					editMode={true}
					price={transaction.amount}
				/>
			)}
		</>
	);
};

export default TransactionPreviewActions;
